import { z } from 'zod';
import { defineStepType } from '../contract.js';
import { isBlankProfileValue } from '../../people/profile-fields.js';
import { fileCategorySchema } from '../../files/categories.js';
import { MAX_EMERGENCY_CONTACTS, personAddressSchema, emergencyContactSchema, employmentHistoryEntrySchema, employmentReferenceSchema, educationEntrySchema, candidateCredentialSchema, } from '../../people/details.js';
export const applicationAttestation = 'I certify that the information in this application is accurate and complete to the best of my knowledge. I understand that HR will review this information and verify the qualifications required for my position.';
const requiredText = z.string().trim().min(1, 'Required').max(200);
export const applicationQuestionSchema = z.object({
    key: z.string().regex(/^[a-z][a-z0-9-]{1,60}$/), label: z.string().trim().min(1).max(300),
    kind: z.enum(['text', 'date', 'yes-no', 'choice']), required: z.boolean().default(false),
    options: z.array(z.string().trim().min(1).max(200)).max(30).default([]),
}).strict();
export const candidateApplicationConfigSchema = z.object({
    heading: z.string().min(1), instructions: z.string().default(''),
    target: fileCategorySchema.default('recruitment-onboarding'),
    attestation: z.string().trim().min(1).max(10000).default(applicationAttestation),
    minReferences: z.number().int().min(0).max(5).default(2),
    requireEmploymentHistory: z.boolean().default(true),
    requireEmergencyContact: z.boolean().default(true),
    requireEducation: z.boolean().default(false),
    requireCredentials: z.boolean().default(false),
    additionalQuestions: z.array(applicationQuestionSchema).max(50).default([]),
}).superRefine((config, ctx) => {
    const keys = new Set();
    config.additionalQuestions.forEach((question, index) => {
        if (keys.has(question.key))
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['additionalQuestions', index, 'key'], message: 'Question IDs must be unique' });
        keys.add(question.key);
        if (question.kind === 'choice' && question.options.length === 0)
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['additionalQuestions', index, 'options'], message: 'Add the available choices' });
    });
});
const applicationAnswers = z.object({
    name: z.object({ firstName: requiredText, middleName: z.string().trim().max(200).default(''), lastName: requiredText, previousName: z.string().trim().max(200).default('') }).strict(),
    email: z.string().trim().email(),
    phone: z.string().trim().min(7).max(50),
    alternatePhone: z.string().trim().max(50).default(''),
    preferredCallTime: z.string().trim().max(200).default(''),
    address: personAddressSchema.extend({ streetAddress: requiredText, city: requiredText, state: requiredText, postalCode: requiredText }),
    emergencyContacts: z.array(emergencyContactSchema).max(MAX_EMERGENCY_CONTACTS).default([]),
    noPriorEmployment: z.boolean().default(false),
    employmentHistory: z.array(employmentHistoryEntrySchema).max(10).default([]),
    references: z.array(employmentReferenceSchema).max(5).default([]),
    education: z.array(educationEntrySchema).max(10).default([]),
    credentials: z.array(candidateCredentialSchema).max(10).default([]),
    additionalAnswers: z.record(z.string(), z.union([z.string().max(4000), z.boolean()])).default({}),
    typedName: requiredText,
    accuracyConfirmed: z.literal(true, { errorMap: () => ({ message: 'Confirm the application is accurate' }) }),
    agreedAt: z.string().datetime(),
    attestedText: z.string().trim().min(1).max(10000),
}).strict();
/**
 * Row-level rules. Takes a PARTIAL value so `sections` can run it over one section's slice of a
 * half-typed draft; on a whole value every key is present (defaults) and it behaves as before.
 */
function validateAnswers(value, ctx) {
    const required = (text, path, message = 'Required') => {
        if (typeof text !== 'string' || !text.trim())
            ctx.addIssue({ code: z.ZodIssueCode.custom, path, message });
    };
    if (value.noPriorEmployment && (value.employmentHistory ?? []).length > 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['employmentHistory'], message: 'Remove the employment rows or clear no prior employment' });
    }
    (value.employmentHistory ?? []).forEach((row, i) => {
        for (const key of ['facility', 'positionHeld', 'fromDate', 'immediateSupervisor', 'reasonForLeaving'])
            required(row[key], ['employmentHistory', i, key]);
        if (row.fromDate && row.toDate && row.fromDate > row.toDate)
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['employmentHistory', i, 'toDate'], message: 'End date must follow start date' });
        if (typeof row.mayContact !== 'boolean')
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['employmentHistory', i, 'mayContact'], message: 'Choose whether HR may contact this employer' });
    });
    (value.references ?? []).forEach((row, i) => {
        for (const key of ['referenceType', 'name', 'relationship'])
            required(row[key], ['references', i, key]);
        if (!row.phone && !row.email)
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['references', i, 'phone'], message: 'Enter a phone number or email' });
        if (typeof row.mayContact !== 'boolean')
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['references', i, 'mayContact'], message: 'Choose whether HR may contact this reference' });
    });
    (value.education ?? []).forEach((row, i) => {
        for (const key of ['institution', 'location', 'degreeEarned', 'fromDate', 'toDate'])
            required(row[key], ['education', i, key]);
        if (row.fromDate && row.toDate && row.fromDate > row.toDate)
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['education', i, 'toDate'], message: 'End date must follow start date' });
    });
    (value.credentials ?? []).forEach((row, i) => {
        for (const key of ['credentialType', 'credentialNumber', 'expirationDate'])
            required(row[key], ['credentials', i, key]);
    });
}
export const candidateApplicationDraftSchema = applicationAnswers.superRefine(validateAnswers);
export const candidateApplicationSubmissionSchema = applicationAnswers.extend({
    uploadId: z.string().min(1), fileName: z.string().min(1),
}).superRefine(validateAnswers);
export const candidateApplication = defineStepType({
    type: 'candidate-application',
    submittedBy: 'person',
    label: 'Candidate application',
    description: 'Contact details, work history, references, education and credentials, with a signed application record.',
    configSchema: candidateApplicationConfigSchema,
    submissionSchema: candidateApplicationSubmissionSchema,
    readyToSubmit: ({ config, draft }) => checkApplicationAnswers(config, draft).ok,
    checkSubmission: async ({ config, submission }) => checkApplicationAnswers(config, submission),
    profileFields: ({ value }) => applicationProfileFields(value),
    sections: ({ config, value }) => applicationSections(config, value),
    /** The flattened application PDF the server staged. Always exactly one. */
    uploads: ({ submission }) => [submission.uploadId],
    onApprove: ({ instance, config, submission }) => [
        { kind: 'store-file', instanceId: instance.id, uploadId: submission.uploadId, category: config.target, source: 'pdf-artifact', plan: null },
        { kind: 'recompute-rollup', personId: instance.personId },
    ],
});
export function checkApplicationAnswers(config, draft) {
    const raw = typeof draft === 'object' && draft !== null ? draft : {};
    const input = Object.fromEntries(Object.entries(raw).filter(([key]) => key !== 'uploadId' && key !== 'fileName'));
    const parsed = candidateApplicationDraftSchema.safeParse(input);
    if (!parsed.success)
        return { ok: false, message: parsed.error.issues.map((issue) => `${issue.path.join('.')}: ${issue.message}`).join('; ') };
    const value = parsed.data;
    const messages = [];
    if (value.attestedText !== config.attestation)
        messages.push('Read and confirm the current application attestation');
    if (config.requireEmploymentHistory && !value.noPriorEmployment && value.employmentHistory.length === 0)
        messages.push('Add employment history or choose no prior employment');
    if (config.requireEmergencyContact && value.emergencyContacts.length === 0)
        messages.push('Add an emergency contact');
    if (value.references.length < config.minReferences)
        messages.push(`Add at least ${config.minReferences} references`);
    if (config.requireEducation && value.education.length === 0)
        messages.push('Add the education required for this position');
    if (config.requireCredentials && value.credentials.length === 0)
        messages.push('Add the credentials required for this position');
    messages.push(...additionalQuestionProblems(config, value.additionalAnswers));
    return messages.length ? { ok: false, message: messages.join('; ') } : { ok: true };
}
/** The pinned version's extra questions, checked against the answers given. */
function additionalQuestionProblems(config, answers) {
    const problems = [];
    for (const key of Object.keys(answers))
        if (!config.additionalQuestions.some((question) => question.key === key))
            problems.push(`Question ${key} is not in this form version`);
    for (const question of config.additionalQuestions) {
        const answer = answers[question.key];
        const blank = answer === undefined || answer === '';
        if (blank) {
            if (question.required)
                problems.push(`Complete ${question.label}`);
            continue;
        }
        if (question.kind === 'yes-no' && typeof answer !== 'boolean')
            problems.push(`Choose yes or no for ${question.label}`);
        if (question.kind !== 'yes-no' && typeof answer !== 'string')
            problems.push(`Enter ${question.label}`);
        if (question.kind === 'choice' && (typeof answer !== 'string' || !question.options.includes(answer)))
            problems.push(`Choose an available answer for ${question.label}`);
        if (question.kind === 'date' && typeof answer === 'string') {
            const day = new Date(`${answer}T00:00:00.000Z`);
            if (!/^\d{4}-\d{2}-\d{2}$/.test(answer) || !Number.isFinite(day.getTime()) || day.toISOString().slice(0, 10) !== answer)
                problems.push(`Enter a valid date for ${question.label}`);
        }
    }
    return problems;
}
const record = (value) => typeof value === 'object' && value !== null && !Array.isArray(value) ? value : {};
/** Rows a half-typed draft already has in a usable shape; blank and malformed rows are left out. */
function usableRows(value, schema) {
    return Array.isArray(value) ? value.filter((row) => !isBlankProfileValue(row) && schema.safeParse(row).success) : [];
}
/**
 * What the application says about the profile (ADR-013 §1). Reads a draft or a submission alike
 * and never decides whether it is reviewed; the projection derives that from the instance.
 * Attestation, the signed record and the extra questions are the form's own, not profile facts.
 */
function applicationProfileFields(value) {
    const raw = record(value);
    const name = record(raw['name']);
    return [
        { field: 'name.givenName', value: name['firstName'] },
        { field: 'name.middleName', value: name['middleName'] },
        { field: 'name.familyName', value: name['lastName'] },
        { field: 'identity.previousName', value: name['previousName'] },
        { field: 'contact.email', value: raw['email'] },
        { field: 'contact.phone', value: raw['phone'] },
        { field: 'contact.alternatePhone', value: raw['alternatePhone'] },
        { field: 'contact.preferredCallTime', value: raw['preferredCallTime'] },
        { field: 'contact.address', value: raw['address'] },
        { field: 'contact.emergencyContacts', value: usableRows(raw['emergencyContacts'], emergencyContactSchema) },
        // "No prior employment" is an answer that there is no history, so any leftover rows are not one.
        { field: 'employmentHistory', value: raw['noPriorEmployment'] === true ? [] : usableRows(raw['employmentHistory'], employmentHistoryEntrySchema) },
        { field: 'references', value: usableRows(raw['references'], employmentReferenceSchema) },
        { field: 'education', value: usableRows(raw['education'], educationEntrySchema) },
        { field: 'credentials', value: usableRows(raw['credentials'], candidateCredentialSchema) },
    ];
}
const APPLICATION_SECTIONS = [
    { key: 'personal', label: 'Personal and contact information', keys: ['name', 'email', 'phone', 'alternatePhone', 'preferredCallTime', 'address'], unmet: () => false },
    { key: 'emergencyContacts', label: 'Emergency contacts', keys: ['emergencyContacts'],
        unmet: (config, a) => config.requireEmergencyContact && (a.emergencyContacts ?? []).length === 0 },
    { key: 'employmentHistory', label: 'Employment history', keys: ['noPriorEmployment', 'employmentHistory'],
        unmet: (config, a) => config.requireEmploymentHistory && !a.noPriorEmployment && (a.employmentHistory ?? []).length === 0 },
    { key: 'references', label: 'References', keys: ['references'],
        unmet: (config, a) => (a.references ?? []).length < config.minReferences },
    { key: 'education', label: 'Education', keys: ['education'],
        unmet: (config, a) => config.requireEducation && (a.education ?? []).length === 0 },
    { key: 'credentials', label: 'Professional licenses and certifications', keys: ['credentials'],
        unmet: (config, a) => config.requireCredentials && (a.credentials ?? []).length === 0 },
    { key: 'additionalQuestions', label: 'Additional questions', keys: ['additionalAnswers'],
        unmet: (config, a) => additionalQuestionProblems(config, a.additionalAnswers ?? {}).length > 0 },
    { key: 'attestation', label: 'Review and sign', keys: ['typedName', 'accuracyConfirmed', 'agreedAt', 'attestedText'],
        unmet: (config, a) => a.attestedText !== config.attestation },
];
/**
 * Each section is checked on its own slice with the same schema and row rules as the whole
 * form, so a section is `done` exactly when nothing in it would stop the form being sent.
 */
function applicationSections(config, value) {
    const raw = record(value);
    const shape = applicationAnswers.shape;
    return APPLICATION_SECTIONS
        .filter((section) => section.key !== 'additionalQuestions' || config.additionalQuestions.length > 0)
        .map((section) => {
        const slice = z.object(Object.fromEntries(section.keys.map((key) => [key, shape[key] ?? z.never()])))
            // Each key's schema is the whole form's, so the parsed slice is a partial of its answers.
            .superRefine((answers, ctx) => validateAnswers(answers, ctx))
            .safeParse(raw);
        const typed = section.keys.some((key) => raw[key] !== false && !isBlankProfileValue(raw[key]));
        const done = slice.success && !section.unmet(config, slice.data);
        return { key: section.key, label: section.label, state: done ? 'done' : typed ? 'in-progress' : 'not-started' };
    });
}
//# sourceMappingURL=candidate-application.js.map